'use client';

import Link from 'next/link';
import {useLocale, useTranslations} from 'next-intl';
import {useState} from 'react';
import {Controller, useForm} from 'react-hook-form';
import {Button} from '@/shared/components/Button';
import {AuthRepository} from '../api/auth.repository';
import {checkIsPhone} from '../helpers/checkIsPhone';
import {isEmail} from '../helpers/isEmail';
import {EmailPhoneInput} from '../ui/EmailPhoneInput';

type ForgotPasswordFormValues = {
  emailOrPhone: string;
};

export const ForgotPasswordForm = () => {
  const [content, setContent] = useState<'form' | 'email'>('form');
  const [isLoading, setIsLoading] = useState(false);
  const locale = useLocale();
  const t = useTranslations('Auth');
  const {
    handleSubmit,
    formState: {errors},
    control,
    watch,
    setError,
  } = useForm<ForgotPasswordFormValues>({
    mode: 'onChange',
    defaultValues: {
      emailOrPhone: '',
    },
  });

  const emailOrPhone = watch('emailOrPhone');
  const isPhone = checkIsPhone(emailOrPhone);

  const onSubmit = async (data: ForgotPasswordFormValues) => {
    if (!isEmail(data.emailOrPhone)) return;
    try {
      setIsLoading(true);
      await AuthRepository.resetPassword({email: data.emailOrPhone});
      setContent('email');
    } catch (error) {
      setError('emailOrPhone', {
        message: error instanceof Error ? error.message : t('Errors.AnUnknownErrorOccurred'),
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (content === 'email') {
    return (
      <div className="flex h-[100%] w-full flex-col items-center justify-center gap-6 p-4 tablet:gap-10 tablet:p-[60px]">
        <div className="flex flex-col items-center justify-center">
          <div className="flex w-full flex-col items-center justify-center gap-4">
            <h1 className="text-fill-transparent bold tablet:leading-1 w-full bg-gradient-to-r from-[#DA3D15] to-[#FE7801] bg-clip-text text-center font-secondary text-[30px] font-extrabold uppercase italic leading-none tablet:text-nowrap tablet:text-[34px]">
              {t('ForgotPassword')}
            </h1>
          </div>
        </div>
        <div className="flex w-full flex-col items-center gap-4">
          <span className="text-center text-[18px] font-bold text-white">{t('CheckEmail.Title')}</span>
          <span className="text-center text-[16px] text-[#AAAAB6]">{t('CheckEmail.Description')}</span>
          <span className="text-center text-[16px] text-[#AAAAB6]">
            {t('CheckEmail.IfYouDontReceiveAnEmailFromUs')}
            <span className="text-white">{t('CheckEmail.ContactCustomerSupport')}</span>.
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-[100%] w-full flex-col items-center justify-center gap-6 p-4 tablet:gap-10 tablet:p-[60px]">
      <div className="flex flex-col items-center justify-center">
        <div className="flex w-full flex-col items-center justify-center gap-4">
          <h1 className="text-fill-transparent bold tablet:leading-1 w-full bg-gradient-to-r from-[#DA3D15] to-[#FE7801] bg-clip-text text-center font-secondary text-[30px] font-extrabold uppercase italic leading-none tablet:text-nowrap tablet:text-[34px]">
            {t('ForgotPassword')}
          </h1>
          <span className="text-center text-[16px] text-[#AAAAB6]">{t('ForgotPasswordDescription')}</span>
        </div>
      </div>
      <form onSubmit={handleSubmit(onSubmit)} className="flex w-full flex-col gap-6">
        <Controller
          control={control}
          name="emailOrPhone"
          rules={{required: 'Please input your Email or Phone!'}}
          render={({field}) => (
            <EmailPhoneInput
              type="email"
              label={t('Email')}
              errorMessage={errors.emailOrPhone?.message}
              {...field}
              placeholder={t('EmailPlaceholder')}
            />
          )}
        />
      </form>
      <div className="flex w-full flex-col items-center gap-4">
        <Button
          size="2md"
          variant="white"
          className="w-full rounded-[14px]"
          disabled={isPhone}
          onClick={handleSubmit(onSubmit)}
          loading={isLoading}
        >
          {t('ResetPassword')}
        </Button>
        <Link href={`/${locale}/auth`} className="w-full">
          <Button
            size="2md"
            variant="ghost"
            className="w-full rounded-[14px] text-white hover:bg-transparent hover:text-white/60"
          >
            {t('Back')}
          </Button>
        </Link>
      </div>
    </div>
  );
};
